<ng-container *ngIf="warranty$ | async as warranty">
  <div class="flex min-h-full flex-col">
    <vs-breadcrumbs
      slot="breadcrumbs"
      [config]="{
      items: [
        { label: warranty.contractNumber, routerLink: '../..' },
        { label: 'Open A New Claim'}
      ]
    }"
    ></vs-breadcrumbs>

    <ng-container [ngSwitch]="step">
      <div class="flex items-center flex-grow" *ngSwitchDefault>
        <div class="flex flex-col w-full justify-center items-center h-full px-8 gap-6">
          <vs-icon name="file-shield-02" class="text-primary-500" size="129"></vs-icon>
          <div class="text-3xl font-semibold text-gray-900">Let's jump right into it!</div>
          <div class="text-xl text-center text-gray-600 max-w-3xl">
            We'll use your answers to the following questions to help expedite your claim processing. Please answer as
            accurately as possible to take full advantage of your
            <span class="font-semibold">{{warranty.policy?.title}}</span>.
          </div>

          <button vs-button theme="primary" (click)="nextPage()">Let's Begin</button>
        </div>
      </div>

      <form
        [formGroup]="claimFormGroup"
        class="flex flex-grow items-center px-8"
        [class.hidden]="step === 0 || step === lastStep"
      >
        <ng-container *ngSwitchCase="1">
          <div class="flex flex-col">
            <div class="flex gap-6">
              <div class="flex gap-2 text-3xl text-primary-500 items-center self-start">
                {{step}}
                <vs-icon name="arrow-right"></vs-icon>
              </div>

              <div class="flex flex-col gap-3">
                <div class="text-3xl font-medium">Describe your issue. *</div>

                <div class="text-2xl">
                  To assist us better handle your claim, please identify the problem you are experiencing.
                </div>

                <input
                  type="text"
                  vsInput
                  name="complaint"
                  formControlName="complaint"
                  placeholder="Describe your issue"
                />

                <div class="text-2xl mt-2">When did the problem start?</div>

                <vs-input-container class="max-w-[280px]">
                  <input formControlName="lossDate" class="picker-indicator-hidden" type="date" [max]="today" />
                  <button
                    vs-icon-button
                    size="xs"
                    appearance="clear"
                    slot="end"
                    [vsMenuTriggerFor]="lossDateMenu"
                    #lossDateTrigger="vsMenuTriggerFor"
                  >
                    <vs-icon vsTooltip="Open date picker" name="calendar" size="16"></vs-icon>
                  </button>
                </vs-input-container>
                <ng-template #lossDateMenu>
                  <vs-menu>
                    <vs-date-picker
                      vs-menu-item
                      [value]="toPickerDate(claimFormGroup.value.lossDate)"
                      [maxDate]="maxDate"
                      [range]="false"
                      (selectionChanged)="pickLossDate($any($event), lossDateTrigger)"
                    ></vs-date-picker>
                  </vs-menu>
                </ng-template>

                <div class="flex gap-2">
                  <button
                    [disabled]="!claimFormGroup.controls.complaint.valid"
                    vs-button
                    theme="primary"
                    (click)="nextPage()"
                  >
                    Next
                    <vs-icon name="arrow-right"></vs-icon>
                  </button>

                  <button vs-button theme="default" appearance="outline" (click)="previousPage()">Back</button>
                </div>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-container *ngSwitchCase="2">
          <div class="flex flex-col">
            <div class="flex gap-6">
              <div class="flex gap-2 text-3xl text-primary-500 items-center self-start">
                {{ step }}
                <vs-icon name="arrow-right"></vs-icon>
              </div>

              <div class="flex flex-col gap-3">
                <div class="text-3xl font-medium">Which product is having the issue? *</div>

                <div class="text-2xl">
                  We're sorry you're experiencing issues. Please select all items related to your complaint.
                </div>

                <div class="flex flex-col gap-2">
                  <ws-coverage-picker
                    #covpicker
                    formControlName="coverages"
                    [warranty]="warranty"
                    [coverageId]="coverageId$ | async"
                    [hasOther]="!!claimFormGroup.value.description?.length"
                  ></ws-coverage-picker>

                  <textarea
                    *ngIf="covpicker.onOther"
                    vsInput
                    min="3"
                    max="6"
                    formControlName="description"
                    placeholder="Describe the issue in greater detail to help identify the coverage"
                  ></textarea>
                </div>

                <div class="flex gap-2">
                  <button
                    vs-button
                    theme="primary"
                    (click)="nextPage()"
                    [disabled]="!claimFormGroup.value.coverages?.length || (checkForOther() && !claimFormGroup.value.description)"
                  >
                    Next
                    <vs-icon name="arrow-right"></vs-icon>
                  </button>
                  <button vs-button theme="default" appearance="outline" (click)="previousPage()">Back</button>
                </div>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-container *ngSwitchCase="3">
          <div class="flex flex-col">
            <div class="flex gap-6">
              <div class="flex gap-2 text-3xl text-primary-500 items-center self-start">
                {{ step }}
                <vs-icon name="arrow-right"></vs-icon>
              </div>

              <div class="flex flex-col gap-3">
                <div class="text-3xl font-medium">Provide evidence for your claim.</div>

                <div class="text-2xl">
                  Upload new photos, or attach documents you have already saved for this contract. The evidence is used
                  to accelerate the approval process and make the remediation seamless for you.
                  <br />
                  <br />
                  If you would not like to provide any evidence, you can skip this step.
                </div>

                <vs-file-input
                  class="max-w-[592px]"
                  formControlName="evidence"
                  [multiple]="true"
                  [accept]="'.SVG, .PNG, .JPG, .JPEG, .GIF'"
                  [moreInfo]="'max. 800x400px'"
                ></vs-file-input>

                <section class="flex flex-col gap-2 max-w-[592px]" aria-labelledby="repository-documents-heading">
                  <div id="repository-documents-heading" class="text-xl font-medium mt-2">
                    Or choose from your documents
                  </div>
                  <p class="text-sm text-gray-600">
                    Attach documents you have already saved for this contract, up to {{ maxRepositoryDocuments }}.
                  </p>

                  <div
                    *ngIf="repositoryError"
                    role="alert"
                    class="flex flex-wrap items-center justify-between gap-3 py-2"
                  >
                    <p class="text-sm text-error-800">Unable to load your documents. You can still continue.</p>
                    <button vs-button appearance="outline" size="sm" type="button" (click)="loadRepositoryDocuments()">
                      Retry
                    </button>
                  </div>

                  <p
                    *ngIf="!repositoryLoading && !repositoryError && !repositoryDocuments.length"
                    role="status"
                    class="text-sm text-gray-500"
                  >
                    You have no saved documents for this contract.
                  </p>

                  <ul
                    *ngIf="repositoryDocuments.length"
                    class="divide-y divide-gray-200 border-y border-gray-200"
                    aria-label="Your documents"
                  >
                    <li *ngFor="let document of repositoryDocuments; trackBy: trackById">
                      <label class="flex items-start gap-3 py-3 cursor-pointer">
                        <input
                          vsInput
                          type="checkbox"
                          class="mt-1"
                          [checked]="isRepositoryDocumentSelected(document.id)"
                          [disabled]="!isRepositoryDocumentSelected(document.id) && repositoryLimitReached"
                          (change)="toggleRepositoryDocument(document.id)"
                        />
                        <span class="flex flex-col">
                          <span class="font-medium text-gray-900 break-words">{{ document.title }}</span>
                          <span class="text-sm text-gray-600">
                            {{ categoryLabels[document.category] }} ·
                            <ng-container *ngIf="document.serviceDate; else uploaded">
                              Service date {{ document.serviceDate + 'T00:00:00Z' | date: 'mediumDate' : 'UTC' }}
                            </ng-container>
                            <ng-template #uploaded>
                              Uploaded {{ document.uploadedAt | date: 'mediumDate' : 'UTC' }}
                            </ng-template>
                          </span>
                        </span>
                      </label>
                    </li>
                  </ul>

                  <div *ngIf="repositoryLoading" role="status" class="text-sm text-gray-500">Loading documents…</div>

                  <div class="flex flex-wrap items-center gap-3">
                    <button
                      *ngIf="repositoryHasMore && !repositoryLoading && !repositoryError"
                      vs-button
                      appearance="outline"
                      size="sm"
                      type="button"
                      (click)="loadRepositoryDocuments()"
                    >
                      Show more
                    </button>
                    <span *ngIf="repositoryDocumentCount" class="text-sm text-gray-600" aria-live="polite">
                      {{ repositoryDocumentCount }} selected
                      <ng-container *ngIf="repositoryLimitReached">(limit reached)</ng-container>
                    </span>
                  </div>
                </section>

                <div class="flex gap-2">
                  <button vs-button theme="primary" (click)="nextPage()">
                    {{ newFileCount || repositoryDocumentCount ? 'Next' : 'Skip' }}

                    <vs-icon name="arrow-right"></vs-icon>
                  </button>
                  <button vs-button theme="default" appearance="outline" (click)="previousPage()">Back</button>
                </div>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-container *ngSwitchCase="4">
          <div class="flex flex-col">
            <div class="flex gap-6">
              <div class="flex gap-2 text-3xl text-primary-500 items-center self-start">
                {{ step }}
                <vs-icon name="arrow-right"></vs-icon>
              </div>

              <div class="flex flex-col gap-3">
                <div class="text-3xl font-medium">Let's review your claim! *</div>
                <div class="flex flex-col gap-1 text-xl text-gray-600">
                  <span>Complaint: {{ claimFormGroup.value.complaint }}</span>
                  <span *ngIf="claimFormGroup.value.lossDate as lossDate">
                    Problem started: {{ lossDate | date: 'mediumDate' }}
                  </span>
                  <div class="flex gap-1">
                    <span>Claim Items:</span>

                    <div class="flex gap-1">
                      <span *ngFor="let coverage of claimFormGroup.value.coverages; index as index">
                        {{ (coverage ? coverage.title : 'Other') + (index < (claimFormGroup.value.coverages?.length ||
                        0) - 1 ? ',' : '') }}
                      </span>
                    </div>
                  </div>

                  <span>New files: {{ newFileCount }}</span>
                  <span>From your documents: {{ repositoryDocumentCount }}</span>
                </div>
                <div class="text-xl font-semibold max-w-[648px]">
                  You understand that to willfully withhold information or deliberately give wrong information
                  constitutes fraud and is a crime punishable by law. You agree that the information provided in this
                  claim is true.
                </div>

                <div class="flex gap-2">
                  <button vs-button theme="primary" (click)="nextPage(warranty.id)">
                    I agree
                    <vs-icon name="check"></vs-icon>
                  </button>

                  <button vs-button theme="default" appearance="outline" (click)="previousPage()">Back</button>
                </div>
              </div>
            </div>
          </div>
        </ng-container>
      </form>

      <ng-container *ngSwitchCase="5">
        <div class="flex flex-col flex-grow w-full justify-center items-center h-full px-8 gap-6">
          <vs-icon name="shield-tick" class="text-primary-500" size="129"></vs-icon>
          <div class="text-3xl font-medium">You have this coverage!</div>
          <div class="text-2xl text-center">
            Good thing you have a
            <span class="font-semibold">{{warranty.policy?.title}}</span>!<br /><br />
            Your claim has been submitted for verification. We're sorry you experienced an issue.<br /><br />
            Please wait while we verify your evidence. You will be redirected automatically.
          </div>
        </div>
      </ng-container>
    </ng-container>
  </div>
</ng-container>
